import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./SnGrid.css";

import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ColDef,
  TextEditorModule,
  AllEnterpriseModule,
  NumberEditorModule,
  CheckboxEditorModule,
  RichSelectModule,
  CellFocusedEvent,
  RowSelectionOptions,
  RowNode,
  CellPosition,
  CustomEditorModule,
  CellKeyDownEvent,
  SuppressKeyboardEventParams,
  Column,
} from "ag-grid-enterprise";
import {
  SnGridProps,
  ColumnConfig,
  SnGridEtcProps,
  SnGridEvents,
  SnTreeEtcProps,
  SnTreeEvents,
  RowData,
  ChangeColumnConfig,
  CustomColDef,
  FlatRowType,
  ChangeGroupColumnConfig,
} from "./SnGridTypes";
import { SnColumnsCreate } from "./SnColumnsCreate";
import SnGridFindBar, { useSnGridFind } from "./SnGridFindBar";
import SnTab from "../SnTab";
import SnTabHeader from "../SnTabHeader";
import { v4 as uuidv4 } from "uuid";
import { global, useMenuStore } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { CustomHeaderCheckbox } from "./SnGridEditors";
import { isDateValue, isNumeric } from "@shared/utils/CommonUtils";
import { SnGridLocaleText } from "./SnGridConstants";
import { PopupHelper } from "./SnGridPopup";
import "./SnGrid.css";

const localeText = SnGridLocaleText;
const SnTree = forwardRef((props: SnGridProps, ref) => {
  const {
    columnConfig = [], // 컬럼 정의(설정이 없는 화면도 있으므로 기본값 필수)
    rowData, // 행 데이터
    etcProps,
    events,
    treeProps,
    treeEvents,
    header,
    filter,
    useTreeTempKey = true,
  } = props;

  //#region 변수모음
  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조
  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API
  const find = useSnGridFind(gridApiRef); // Ctrl+F 찾기
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const usedKeys = new Set<string>(); // 이미 사용된 키를 추적
  const { userInfo } = global(); // 전역 사용자 정보
  const modifiedRowsRef = useRef({
    added: [] as RowData[],
    updated: [] as RowData[],
    deleted: [] as RowData[],
  });
  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    if (etcProps?.rowSelection === "single") {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else if (etcProps?.rowSelection === "multiple") {
      return {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      }; // 기본값
    }
  }, [etcProps?.rowSelection]);
  const isRowDataFullSet = useRef(false);
  const isDeleteFocusPending = useRef({
    pending: false, // 삭제 후 포커스 대기 여부
    index: 0, // 포커스할 대상 인덱스
  });

  const [keyRowData, setKeyRowData] = useState<RowData[]>([]);
  const [recoveryData, setRecoveryData] = useState<any>({});
  const [initPinnedBottomRowData, setInitPinnedBottomRowData] = useState<any>(
    []
  );

  const [isMemoizedRowDataChanged, setIsMemoizedRowDataChanged] =
    useState<any>(false);
  const popupCons = useRef<any>({});

  const memoizedRowData = useMemo(() => rowData, [rowData]);
  const popupContainer = useMemo(() => {
    // ID가 'ag-grid-popup-root'인 요소를 찾아 반환
    return document.querySelector("#ag-grid-popup-root");
  }, []);
  const [focusedRowNode, setFocusedRowNode] = useState<RowNode>();
  const isPopupOpen = useRef(false);
  //#endregion

  //#region 컬럼 정의 설정
  useEffect(() => {
    const loadColumnDefs = async () => {
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      const generatedColumnDefs = await SnColumnsCreate(
        columnConfig,
        etcProps?.isEditable ?? true
      );
      // updatedColumnDefs = generatedColumnDefs;
      updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
        ...colDef,
      }));

      if (etcProps?.ischeckboxSelection) {
        updatedColumnDefs = [
          {
            headerName: "",
            cellRendererSelector: (params: any) => {
              if (params.node?.rowPinned) {
                return { component: "" }; // footer는 아무것도 안 보여줌
              }
              return { component: "agCheckboxCellRenderer" };
            },
            cellEditor: "agCheckboxCellEditor",
            width: 20,
            colId: "fixCheckbox",
            field: "fixCheckbox",
            headerComponent: CustomHeaderCheckbox,
            editable: true,
            cellStyle: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          },
          ...updatedColumnDefs,
        ];
      }
      setColumnDefs(updatedColumnDefs);

      const sums = columnConfig.filter((col) => col.yn_sum === "Y");
      if (sums.length > 0) {
        setInitPinnedBottomRowData([
          {
            fixIndicator: 0,
          },
        ]);
      } else {
        setInitPinnedBottomRowData([]);
      }
    };

    loadColumnDefs();
  }, [columnConfig]);
  //#endregion

  //#region
  useEffect(() => {
    setKeyRowData(
      (memoizedRowData || []).map((row) => {
        const key = row.KeyField || generateKey();

        return etcProps?.ischeckboxSelection
          ? { ...row, KeyField: key, fixCheckbox: false }
          : { ...row, KeyField: key };
      })
    );
    setIsMemoizedRowDataChanged(true);
  }, [memoizedRowData]);
  //#endregion

  //#region 공통메서드
  const generateKey = () => {
    let key;
    do {
      key = uuidv4(); // 항상 새로운 UUID 생성
    } while (usedKeys.has(key)); // 중복 여부 확인
    usedKeys.add(key);
    return key;
  };

  const handleRowFocusAfterDelete = (deletedRowIndex: number) => {
    if (!gridApiRef.current) return;
    const totalRowCount = gridApiRef.current.getDisplayedRowCount();

    if (totalRowCount === 0) return;

    let nextFocusIndex = deletedRowIndex;

    // 1. 삭제된 인덱스가 현재 총 행 수를 초과하는 경우 (마지막 행을 삭제한 경우)
    if (deletedRowIndex >= totalRowCount) {
      // 남아있는 가장 마지막 행으로 포커스
      nextFocusIndex = totalRowCount - 1;
    }
    // 2. 삭제된 인덱스가 유효한 경우 (그 아래 행이 남아있는 경우)
    else {
      // 삭제된 행의 자리로 포커스를 이동합니다.
      nextFocusIndex = deletedRowIndex;
    }

    // 포커스/선택 초기화 (이벤트 핸들러에서 수행하는 것이 더 안전합니다)
    gridApiRef.current.clearFocusedCell();
    gridApiRef.current.deselectAll();

    // 포커스 설정
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(nextFocusIndex);
    const firstCol = gridApiRef.current.getAllDisplayedColumns()[0];

    if (rowNode && firstCol) {
      gridApiRef.current.ensureIndexVisible(nextFocusIndex);
      gridApiRef.current.setFocusedCell(nextFocusIndex, firstCol.getColId());
      rowNode.setSelected(true);
    }
  };

  const handleModifiedRowsDelete = (deletedRows: RowData[]) => {
    deletedRows.forEach((row) => {
      // '추가'된 행이면 -> 'added' 목록에서 제거
      const addedIndex = modifiedRowsRef.current.added.findIndex(
        (r) => r.KeyField === row.KeyField
      );
      if (addedIndex !== -1) {
        modifiedRowsRef.current.added.splice(addedIndex, 1);
      } else {
        const updatedIndex = modifiedRowsRef.current.updated.findIndex(
          (r) => r.KeyField === row.KeyField
        );
        if (updatedIndex !== -1) {
          modifiedRowsRef.current.updated.splice(updatedIndex, 1);
        }
        modifiedRowsRef.current.deleted.push(row);
      }
    });
  };

  const calculateTotals = async () => {
    const sums = columnConfig.filter((col) => col.yn_sum === "Y");

    if (!gridApiRef.current || sums.length === 0) return;

    const totalRow: Record<string, number> = {};

    sums.forEach((sumCol) => {
      totalRow[sumCol.cd_column] = 0;
    });

    gridApiRef.current.forEachNodeAfterFilter((node: RowNode) => {
      const row = node.data; // 필터링된 행 데이터

      sums.forEach((sumCol) => {
        const key = sumCol.cd_column;
        totalRow[key] += parseFloat(row[key]) || 0;
      });
    });

    const result = [totalRow];

    gridApiRef.current.setGridOption("pinnedBottomRowData", result);
    gridApiRef.current.refreshCells({
      force: true, // valueGetter/valueFormatter를 강제로 다시 실행합니다.
      columns: ["fixIndicator"], // 순번 컬럼의 colId 사용
    });

    if (events?.onAfterSetFooter) {
      events.onAfterSetFooter(totalRow);
    }
  };

  const checkEditable = (rowData: any, colDef: any): boolean => {
    const isEditable =
      typeof colDef.editable === "function"
        ? colDef.editable({
            data: rowData,
            colDef: colDef,
          })
        : true;
    return isEditable;
  };

  const moveFocusToNextEditableCell = (data: any, column: Column) => {
    if (!gridApiRef.current || !column) return;
    if (!gridApiRef.current.getFocusedCell()) return;
    const isCurrentCellEditable = checkEditable(data, column.getColDef());

    if (!column.getColId().startsWith("no_")) {
      if (isCurrentCellEditable) {
        return;
      }
    }

    const moved = gridApiRef.current.tabToNextCell();

    if (moved) {
      requestAnimationFrame(() => {
        moveFocusToNextEditableCell(
          data,
          gridApiRef.current.getFocusedCell().column
        );
      });
    }
  };

  const moveToNextCell = (data: any, colId: string) => {
    if (!checkLastColumn(colId)) {
      const moved = gridApiRef.current.tabToNextCell();

      if (moved) {
        requestAnimationFrame(() => {
          moveFocusToNextEditableCell(
            data,
            gridApiRef.current.getFocusedCell().column
          );
        });
      }
    } else {
      addRow();
    }
  };
  const openPopup = async (params: any) => {
    if (isPopupOpen.current) return; // 중복 실행 방지
    if (!gridApiRef.current) return;
    const isCurrentCellEditable = checkEditable(
      params.api.getRowNode(params.node.id).data,
      params.colDef
    );
    if (!isCurrentCellEditable) {
      return;
    }
    isPopupOpen.current = true;
    try {
      const { applyNodes, isMove } = await PopupHelper(
        gridApiRef,
        params,
        popupCons.current,
        columnConfig,
        addRow
      );
      gridApiRef.current.stopEditing();
      if (isMove) {
        const len = applyNodes.length;
        moveToNextCell(applyNodes[len - 1].data, params.colDef.field);
      }
      events?.onAfterPopupClose &&
        events.onAfterPopupClose({
          nodes: applyNodes,
          colId: params.colDef.field,
        });
    } finally {
      isPopupOpen.current = false; // 오류가 나도 해제되도록
    }
  };

  const checkLastColumn = (colId: string) => {
    const displayedCols = gridApiRef.current.getAllDisplayedColumns();
    const isLastDisplayed =
      displayedCols[displayedCols.length - 1].getColId() === colId;
    return isLastDisplayed;
  };
  //#endregion

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    addRow,
    deleteSelectedRows,
    deleteRowByIndex,
    deleteAllRows,
    getChange,
    resetUpdate,
    selectAndFocusRow,
    selectAllFiltered,
    getSelectedRows,
    getRowCount: () => {
      return gridApiRef.current?.getDisplayedRowCount() || 0;
    },
    stopEditing: () => gridRef.current?.api.stopEditing(),
    changeColumnConfigs,
    requiredValuesCheck,
    applyBlock,
    clearBlock,
    getMaxValue,
    getCheckedRows,
    applyFilters,
    resetFilters,
    getFooterData,
    getFlatDataFromGrid,
    onGroupExpandedOrCollapsed: () =>
      gridRef.current?.api.onGroupExpandedOrCollapsed(),
    getSelectedNodes: () => gridRef.current?.api.getSelectedNodes(),
    getDisplayedRowAtIndex: (index: number) => {
      return gridRef.current?.api.getDisplayedRowAtIndex(index);
    },
    setExpandedAll: (isExpand: boolean) => setExpandedAll(isExpand),
    applyPopupConditions: (popupConditions: any) =>
      (popupCons.current = popupConditions),
    sharedCellValueChanged,
    focusedRowNode: () => focusedRowNode,
    getRowNodeAtIndex: (Index: number) =>
      gridApiRef.current.getDisplayedRowAtIndex(Index),
    getRowNode: (key: string) => gridApiRef.current.getRowNode(key),
    getAllGridColumns: () => gridApiRef.current.getAllGridColumns(),
    calculateTotals,
    resetGrid,
    forEachFilteredNode: (callback: (node: RowNode) => void) => {
      gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
        callback(node); // 여기서 바로 처리
      });
    },
    changeGroupColumnConfigs,
    scrollToIndex: (index: number) =>
      gridApiRef.current.ensureIndexVisible(index),
    setFocusedCell: (index: number, colId: string) =>
      gridApiRef.current.setFocusedCell(index, colId),
    forEachNode: (callback: (node: RowNode) => void) => {
      gridApiRef.current?.forEachNode((node: RowNode) => {
        callback(node); // 여기서 바로 처리
      });
    },
    clearFocus: () => {
      gridApiRef.current.clearFocusedCell();
      gridApiRef.current.deselectAll();
    },
    getAllDisplayedColumns: () => gridApiRef.current.getAllDisplayedColumns(),
    exportDataAsExcel: (params: any) =>
      gridApiRef.current.exportDataAsExcel(params),
    applyTransaction: (params: any) =>
      gridApiRef.current.applyTransaction(params),
    setCheckedAll,
    setCheckedFocusedRow: (check: boolean, source?: string) =>
      focusedRowNode?.setDataValue("fixCheckbox", check, source),
  }));

  const setExpandedAll = (isExpand: boolean) => {
    gridRef.current?.api.forEachNode((node: any) => node.setExpanded(isExpand));
  };

  const addRow = (
    newRow: RowData = {},
    parentNode: any = null
  ): RowNode | null => {
    if (!gridApiRef.current) return null;
    const api = gridRef.current?.api;
    if (!api) return null;

    api.stopEditing();
    api.clearCellSelection(); // 행추가하면서 기존 선택된 셀포커스 제거
    api.deselectAll(); // 행추가하면서 기존 선택된 로우포커스 제거

    //  화면에서 넘긴 onBeforeAddRow 훅 있을 때만 실행
    if (events?.onBeforeAddRow) {
      const rtn = events.onBeforeAddRow();
      if (typeof rtn === "boolean" && rtn === false) {
        return null;
      }
      if (typeof rtn === "object" && rtn) {
        Object.assign(newRow, rtn);
      }

      // const selectedNodes = api.getSelectedNodes();

      // console.log(selectedNodes);

      // if (selectedNodes && selectedNodes.length > 0 && parentNode === null) {
      //   parentNode = selectedNodes[0];
      // }
    }

    const treeIdKey = treeProps?.treeIdKey;
    const treeDataParentIdField = treeProps?.treeDataParentIdField;
    if (!treeIdKey || !treeDataParentIdField) return null;

    // 새 행 데이터 준비
    const newKey = generateKey();

    const resolvedTreeId = useTreeTempKey
      ? "temp-" + newKey.slice(0, 8)
      : newRow?.[treeIdKey];

    const newData = {
      ...newRow,
      KeyField: newKey,
      [treeIdKey]: resolvedTreeId,
      state: "insert",
    };

    // 부모 정보 지정
    if (parentNode !== null) {
      newData[treeDataParentIdField] = parentNode
        ? (parentNode.data?.[treeIdKey] ?? null)
        : null;
    }

    // 전체 데이터 반영
    const allRows: any[] = [];
    api.forEachNode((node: any) => allRows.push(node.data));
    allRows.push(newData);
    gridApiRef.current.setGridOption("rowData", allRows);

    // 수정 리스트에 등록
    modifiedRowsRef.current.added.push(newData);

    // 새로 추가된 RowNode focus
    let targetNode: any = null;
    gridApiRef.current.forEachNode((node: any) => {
      if (node.data?.KeyField === newKey) {
        targetNode = node;
      }
    });
    if (targetNode) selectAndFocusRow(targetNode.rowIndex as number);
    return targetNode;
  };

  const deleteSelectedRows = async () => {
    const api = gridApiRef.current;
    if (!api) return;

    const selectedNodes = api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) return;

    // 하위 존재 여부
    const hasChildren = selectedNodes.some(
      (node: any) => node.allLeafChildren?.length > 0
    );
    if (hasChildren) {
      const confirm = await showDialog(
        "confirm",
        "하위에 구성된 내역도 삭제됩니다.\n삭제를 진행하시겠습니까?"
      );
      if (!confirm) return;
    }

    // 삭제 대상 수집 (부모 + 모든 하위)
    const rowsToRemove: any[] = [];
    const visited = new Set<string>();

    const collectNode = (node: any) => {
      const id = String(node.data.no_bs);
      if (visited.has(id)) return;
      visited.add(id);

      // 본인 추가
      rowsToRemove.push(node.data);

      // 하위 추가
      if (node.childrenAfterGroup?.length > 0) {
        // node.childrenAfterGroup.forEach((child: any) => collectNode(child));
        node.childrenAfterGroup.forEach((child: any) =>
          rowsToRemove.push(child.data)
        );
      }
    };
    selectedNodes.forEach((node: any) => collectNode(node));
    if (rowsToRemove.length === 0) return;

    // 포커스 복원 정보
    const firstSelectedIndex = selectedNodes[0]?.rowIndex ?? 0;
    isDeleteFocusPending.current = {
      pending: true,
      index: firstSelectedIndex,
    };

    // TreeData 삭제 (부모 + 자식 전부)
    api.applyTransaction({
      remove: rowsToRemove,
    });

    handleModifiedRowsDelete(rowsToRemove);
    setRecoveryData({
      rows: rowsToRemove,
      indexes: rowsToRemove.map((r: any) => r.rowIndex),
    });

    calculateTotals();
  };

  const deleteRowByIndex = (rowIndex: number) => {
    if (!gridApiRef.current) return;

    // 1. RowNode 가져오기
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(rowIndex);

    if (!rowNode || !rowNode.data) {
      return;
    }
    const rowToRemove = rowNode.data;
    const rowsToRemoveIndexes = rowNode.rowIndex;
    // 3. 포커스 플래그 설정
    // 삭제되기 전의 인덱스를 기준으로 포커스를 설정하도록 플래그에 저장
    isDeleteFocusPending.current = {
      pending: true,
      index: rowIndex, // 삭제된 행의 인덱스를 저장
    };

    // 4. AG Grid에서 행 삭제 요청
    gridApiRef.current.applyTransaction({ remove: [rowToRemove] });
    handleModifiedRowsDelete([rowToRemove]);
    setRecoveryData({ rows: [rowToRemove], indexes: [rowsToRemoveIndexes] });
    calculateTotals();
  };

  const deleteAllRows = () => {
    if (!gridApiRef.current) return;

    const rowsToRemove: any[] = [];
    const rowsToRemoveIndexes: number[] = [];

    gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
      rowsToRemove.push(node.data);
      rowsToRemoveIndexes.push(node.rowIndex!);
    });

    if (rowsToRemove.length === 0) {
      return;
    }
    gridApiRef.current.applyTransaction({ remove: rowsToRemove });
    handleModifiedRowsDelete(rowsToRemove);
    setRecoveryData({ rows: rowsToRemove, indexes: rowsToRemoveIndexes });
    calculateTotals();
  };

  const getChange = () => {
    return {
      modifiedRowcount:
        modifiedRowsRef.current.added.length +
        modifiedRowsRef.current.updated.length +
        modifiedRowsRef.current.deleted.length,
      insert: modifiedRowsRef.current.added,
      update: modifiedRowsRef.current.updated,
      delete: modifiedRowsRef.current.deleted,
    };
  };

  const resetUpdate = (isRecovery?: boolean) => {
    modifiedRowsRef.current = { added: [], updated: [], deleted: [] };
    if (gridApiRef.current == null) return;
    if (isRecovery) {
      if (gridRef.current?.api == null) return;
      if (recoveryData.rows == null || recoveryData.indexes == null) return;
      const rows: any[] = recoveryData.rows;
      const indexes: any[] = recoveryData.indexes;
      rows.forEach((row, i) => {
        gridRef.current?.api.applyTransaction({
          add: [{ ...row }],
          addIndex: indexes[i],
        });
      });
      selectAndFocusRow(indexes[0]);
    } else {
      const updatedRows: any[] = [];

      gridApiRef.current.forEachNode((node: RowNode) => {
        const data = { ...node.data };
        delete data.block;
        delete data.state;
        updatedRows.push(data);
      });

      gridApiRef.current.applyTransaction({ update: updatedRows });
    }
  };

  const getSelectedRows = () => {
    const selectedRows = gridApiRef.current?.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const selectAndFocusRow = (index: number) => {
    if (!gridApiRef.current) return;

    const totalRowCount = gridApiRef.current.getDisplayedRowCount();
    if (index < 0 || index >= totalRowCount) return;

    // 1. 행 선택 (rowSelection이 'singleRow'라면 기존 선택은 자동으로 해제됨)
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(index);
    if (rowNode) {
      rowNode.setSelected(true, true); // 두 번째 인자(clearSelection)를 true로 하여 기존 선택 해제
    }

    // 2. 행이 보이도록 스크롤

    // 3. 포커스를 해당 행의 첫 번째 컬럼(또는 원하는 컬럼)으로 설정
    // 행 인덱스와 컬럼 ID가 필요합니다.
    const firstCol = gridApiRef.current.getAllDisplayedColumns()[1];

    if (firstCol) {
      requestAnimationFrame(() => {
        gridApiRef.current.ensureIndexVisible(index);
        gridApiRef.current.setFocusedCell(index, firstCol.colId);
      });
    }
  };

  const selectAllFiltered = () => {
    // 1. Grid API 유효성 검사 (기존과 동일)
    if (!gridApiRef.current) {
      return;
    }
    gridApiRef.current.clearFocusedCell();
    gridApiRef.current.deselectAll(); // 기존 선택된 모든 행 초기화
    gridApiRef.current.selectAll("filtered");
  };

  const requiredValuesCheck = () => {
    try {
      // 26.09.22 kdy 필수값 체크는 화면에 보이는 컬럼만 대상으로 한다.
      // (fg_visible 이 N/H 인 숨김 컬럼, 사용자가 헤더 메뉴로 숨긴 컬럼은 제외)
      const requireds = columnConfig.filter((col) => {
        if (col.yn_required !== "Y") return false;
        const gridCol = gridApiRef.current?.getColumn(col.cd_column);
        return gridCol ? gridCol.isVisible() : col.fg_visible === "Y";
      });

      modifiedRowsRef.current.added.forEach((change) => {
        requireds.forEach((required) => {
          const key = required.cd_column; // 또는 nm_column, 실제 데이터 키에 맞게 조정
          const value = change[key];

          if (value == null || value === "") {
            showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
            throw "finish";
          }
        });
      });

      // 26.09.22 kdy 수정 목록에는 화면 코드가 setDataValue("noEvent")로 값을 밀어 넣은 행도
      // 들어오므로, 사용자가 직접 편집해 state 가 "update" 로 표시된 행만 검사한다.
      // (예전에 빈 값으로 저장돼 있던 행이 헤더 값 전파만으로 저장이 막히지 않도록)
      modifiedRowsRef.current.updated.forEach((change) => {
        if (change?.state !== "update") return;
        requireds.forEach((required) => {
          const key = required.cd_column; // 또는 nm_column, 실제 데이터 키에 맞게 조정
          const value = change[key];

          if (value == null || value === "") {
            showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
            throw "finish";
          }
        });
      });

      return true;
    } catch (Exception) {
      if (Exception !== "finish") throw Exception;
      else return false;
    }
  };

  const getMaxValue = (column: string) => {
    if (!gridApiRef.current) {
      return;
    }
    let maxNoLine: number = 0;
    gridApiRef.current.forEachNodeAfterFilterAndSort((node: RowNode) => {
      const value = node.data[column];
      if (value != null) {
        if (maxNoLine === null || value > maxNoLine) {
          maxNoLine = value;
        }
      }
    });
    return maxNoLine;
  };

  const applyBlock = (node: any) => {
    if (!node || !gridApiRef.current) return;
    const updatedRow = { ...node.data, block: true };
    gridApiRef.current.applyTransaction({ update: [updatedRow] });
  };

  const clearBlock = (node: any) => {
    if (!node || !gridApiRef.current) return;
    const updatedRow = { ...node.data, block: false };
    gridApiRef.current.applyTransaction({ update: [updatedRow] });
  };

  const changeColumnConfigs = (columnSettings: ChangeColumnConfig[]) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    const allColumns = gridApi.getAllGridColumns();
    if (!allColumns) return;

    // applyColumnState용 배열 준비
    const columnStateUpdates: any[] = [];

    // 1. colDef 수정 및 columnState 업데이트 준비
    allColumns.forEach((col: any) => {
      const colDef = col.getColDef();
      const field = colDef.field;
      const setting = columnSettings.find((s) => s.cd_column === field);
      if (!setting) return;

      // headerName 변경
      if (setting.nm_column !== undefined) {
        colDef.headerName = setting.nm_column;
      }

      // visible 설정
      if (setting.fg_visible !== undefined) {
        const hide = setting.fg_visible !== "Y";

        // colDef도 바꿔주고
        colDef.hide = hide;

        // 실제 컬럼 상태도 업데이트해야 함
        columnStateUpdates.push({
          colId: field,
          hide: hide,
        });
      }
    });

    // 2. 실제 컬럼 상태 적용 (이걸 하지 않으면 화면에 안 보임)
    if (columnStateUpdates.length > 0) {
      gridApi.applyColumnState({
        state: columnStateUpdates,
        applyOrder: false,
      });
    }

    // 3. 화면 갱신
    gridApi.refreshHeader();
    gridApi.refreshCells({ force: true });
  };

  const changeGroupColumnConfigs = (
    columnSettings: ChangeGroupColumnConfig[]
  ) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    // 1. 기존 columnDefs 가져오기
    const currentDefs = gridApi.getColumnDefs();
    if (!currentDefs) return;

    // 2. group & child 컬럼 모두 설정 반영
    const updatedDefs = currentDefs.map((colDef: any) => {
      // 그룹 컬럼이면 children 처리
      if (colDef.children) {
        const updatedChildren = colDef.children.map((child: any) => {
          const setting = columnSettings.find(
            (s) => s.cd_column === child.field
          );
          if (!setting) return child;

          return {
            ...child,
            headerName:
              setting.nm_column !== undefined
                ? setting.nm_column
                : child.headerName,
            hide:
              setting.fg_visible !== undefined
                ? setting.fg_visible !== "Y"
                : child.hide,
          };
        });

        return {
          ...colDef,
          children: updatedChildren,
        };
      }

      // 일반 단일 컬럼 처리
      const setting = columnSettings.find((s) => s.cd_column === colDef.field);
      if (!setting) return colDef;

      return {
        ...colDef,
        headerName:
          setting.nm_column !== undefined
            ? setting.nm_column
            : colDef.headerName,
        hide:
          setting.fg_visible !== undefined
            ? setting.fg_visible !== "Y"
            : colDef.hide,
      };
    });

    // 3. setColumnDefs 필수 (v34는 이것만이 확실하게 반영)
    setColumnDefs(updatedDefs);
  };

  const getCheckedRows = () => {
    const checkedRows: any[] = [];
    gridApiRef.current?.forEachNode((node: RowNode) => {
      if (!node.rowPinned && node.data.fixCheckbox) {
        checkedRows.push(node.data);
      }
    });
    return checkedRows;
  };

  const applyFilters = (filterConditions: { [key: string]: any }) => {
    if (gridApiRef.current) {
      const filterModel: { [key: string]: any } = {};

      Object.keys(filterConditions).forEach((field) => {
        const value = filterConditions[field];

        if (value === "" || value === undefined) {
          filterModel[field] = { type: "blank" };
        } else if (Array.isArray(value)) {
          // 배열인 경우: Set Filter 방식 사용
          filterModel[field] = {
            filterType: "set",
            values: value,
          };
        } else {
          // 단일 값인 경우
          filterModel[field] = {
            type: "equals",
            filter: value,
          };
        }
      });

      gridApiRef.current.setFilterModel(filterModel);

      calculateTotals();
    }
  };

  const resetFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      gridApiRef.current.onFilterChanged();

      calculateTotals();
    }
  };

  const getFooterData = () => {
    if (gridApiRef.current) {
      const pinnedBottomRowData = gridApiRef.current.getGridOption(
        "pinnedBottomRowData"
      );
      return pinnedBottomRowData[0];
    }
    return null;
  };

  const resetGrid = () => {
    gridRef.current?.api.clearCellSelection();
    gridRef.current?.api.deselectAll();
  };
  const getFlatDataFromGrid = (
    columnKeys: string[]
  ): Map<string, FlatRowType> => {
    const flatRowMap = new Map<string, FlatRowType>();

    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const raw = node.data;
      const flat: FlatRowType = {};

      columnKeys.forEach((key) => {
        const rawVal = raw[key];

        let val = raw[key];
        let displayValue = rawVal;

        if (typeof val === "object" && val !== null) {
          val = val.label ?? val.text ?? val.value ?? JSON.stringify(val);
        }

        if (isDateValue(val)) {
          flat[key] = {
            value:
              val.length === 8
                ? `${val.slice(0, 4)}/${val.slice(4, 6)}/${val.slice(6, 8)}`
                : val,
            dataType: "dateTime" as const,
          };
        }
        //  앞에 0이 붙은 코드 → 먼저 처리
        else if (
          typeof val === "string" &&
          /^\d+$/.test(val) &&
          val.startsWith("0")
        ) {
          flat[key] = {
            value: `'${val}`, // 또는 '=' + '"' + val + '"'
            dataType: "string",
          };
        } else if (isNumeric(val)) {
          flat[key] = {
            value: Number(val),
            dataType: "number" as const,
          };
        } else {
          //  앞자리 0 보존을 위한 처리

          flat[key] = {
            value: val ?? "",
            dataType: "string" as const,
          };
        }
      });

      flatRowMap.set(node.id!, flat);
    });

    return flatRowMap;
  };

  const setCheckedAll = (check: boolean) => {
    gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
      node.setDataValue("fixCheckbox", check, "noEvent");
    });
  };

  const sharedCellValueChanged = (change: any) => {
    if (!gridApiRef.current) return;
    const api = gridApiRef.current;

    let targetRowNode: RowNode | null = null;
    api.forEachNode((node: any) => {
      if (node.data.KeyField === change.KeyField) {
        targetRowNode = node;
      }
    });
    if (!targetRowNode) return;

    const updatedData = {
      // ...targetRowNode.data,
      ...change,
    };

    api.applyTransaction({ update: [updatedData] });

    const addedIdx = modifiedRowsRef.current.added.findIndex(
      (row) => row.KeyField === change.KeyField
    );
    const updatedIdx = modifiedRowsRef.current.updated.findIndex(
      (row) => row.KeyField === change.KeyField
    );

    if (addedIdx !== -1) {
      modifiedRowsRef.current.added[addedIdx] = updatedData;
    } else if (updatedIdx !== -1) {
      modifiedRowsRef.current.updated[updatedIdx] = updatedData;
    } else {
      modifiedRowsRef.current.updated.push(updatedData);
    }

    // 2. 합계 재계산
    calculateTotals();
  };
  //#endregion

  //#region 그리드 이벤트
  const handleCellFocused = async (event: CellFocusedEvent) => {
    const currentRowNode = gridApiRef.current.getDisplayedRowAtIndex(
      event.rowIndex
    );

    if (!currentRowNode) return;
    setFocusedRowNode(currentRowNode);
    if (events?.onFocusedRowChanged && currentRowNode != focusedRowNode) {
      events.onFocusedRowChanged(currentRowNode.data);
      if (filter?.refs && filter?.columns) {
        filter.refs.map((fiterRef: any) => {
          fiterRef.current.applyFilters(filter.columns);
        });
      }
    }
    function hasColDef(obj: any): obj is { colDef: any } {
      return obj && typeof obj === "object" && "colDef" in obj;
    }

    const column = event.column;

    if (hasColDef(column)) {
      const colDef = column.colDef;
      const params = { api: event.api, colDef: colDef, node: currentRowNode };
      const tpColumn = colDef?.columnConfig?.tp_column;
      const isCodeBox =
        tpColumn === "SnCodeBox" || tpColumn === "SnMultiCodeBox";
      if (
        (isCodeBox && !currentRowNode.data[colDef.field]) ||
        (isCodeBox && currentRowNode.data[colDef.field] == "")
      ) {
        openPopup(params);
      }
    }
  };

  const handleInternalCellValueChanged = async (event: any) => {
    if (!gridApiRef.current) return;

    if (event.colDef?.field === "fixCheckbox") {
      if (event.source !== "noEvent") {
        selectAndFocusRow(event.node.rowIndex);
        if (events?.onCheckedRowChaged) {
          events.onCheckedRowChaged(event.newValue);
        }
      }
      return;
    }
    gridApiRef.current.applyTransaction({ update: [event.data] });

    if (events?.onCellValueChanged && event.source !== "noEvent") {
      const data = {
        rowData: event.data,
        newValue: event.newValue,
        oldValue: event.oldValue,
        rowIndex: event.rowIndex,
        colId: event.colDef.field,
        rowNode: event.node,
      };
      events.onCellValueChanged(data);
    }

    // 26.09.22 kdy 사용자 편집으로 바뀐 기존 행은 state 를 "update" 로 표시한다(noEvent 전파 제외).
    if (event.source !== "noEvent" && event.data?.state !== "insert") {
      event.data.state = "update";
    }
    const index = modifiedRowsRef.current.added.findIndex(
      (row) => row.KeyField === event.data.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRowsRef.current.added[index] = event.data;
    } else {
      const index = modifiedRowsRef.current.updated.findIndex(
        (row) => row.KeyField === event.data.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRowsRef.current.updated[index] = event.data;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRowsRef.current.updated.push(event.data);
      }
    }
    if (filter?.refs && filter?.columns) {
      if (filter.columns.includes(event.colDef.field)) {
        filter.refs.map((fiterRef: any) => {
          fiterRef.current.applyFilters(filter.columns);
        });
      }
    }
    calculateTotals();
  };

  const handleGridReady = (params: any) => {
    const { api, columnApi } = params;
    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;
  };

  const handleInternalRowDragEnd = async (event: any) => {
    if (!gridApiRef.current) return;
    if (treeEvents?.onRowDragEnd) {
      treeEvents?.onRowDragEnd(event);
    }

    const api = event.api;
    const movedNode = event.node;
    const treeIdKey = treeProps?.treeIdKey;
    const treeOrderKey = treeProps?.treeOrderKey;
    const treeDataParentIdField = treeProps?.treeDataParentIdField;

    if (
      !api ||
      !movedNode ||
      !treeIdKey ||
      !treeOrderKey ||
      !treeDataParentIdField
    )
      return;

    const parentNode = movedNode.parent;
    const parentId = parentNode?.data?.[treeIdKey] ?? null;

    const siblings =
      parentNode?.childrenAfterGroup ??
      api.rowModel.rootNode.childrenAfterGroup;

    siblings.forEach((node: any, index: number) => {
      const data = node.data;
      const newParent = parentId;
      const newOrder = index + 1;
      let changed = false;

      // 데이터 직접 수정 (setDataValue 대신)
      if (data[treeDataParentIdField] !== newParent) {
        data[treeDataParentIdField] = newParent;
        changed = true;
      }

      if (data[treeOrderKey] !== newOrder) {
        data[treeOrderKey] = newOrder;
        changed = true;
      }

      // modifiedRowsRef 등록
      if (changed) {
        const isAdded = modifiedRowsRef.current.added.some(
          (r) => r[treeIdKey] === data[treeIdKey]
        );
        if (isAdded) return;

        const updatedList = modifiedRowsRef.current.updated;
        const existingIdx = updatedList.findIndex(
          (r) => r[treeIdKey] === data[treeIdKey]
        );

        if (existingIdx >= 0) {
          updatedList[existingIdx] = { ...data, state: "update" };
        } else {
          updatedList.push({ ...data, state: "update" });
        }
      }
    });

    // 트리 구조 다시 계산
    // refreshClientSideRowModel이 핵심임
    api.refreshClientSideRowModel("group");
    api.onGroupExpandedOrCollapsed();
  };

  const handleCellDoubleClicked = async (params: any) => {
    if (
      params.colDef.columnConfig?.tp_column === "SnCodeBox" ||
      params.colDef.columnConfig?.tp_column === "SnMultiCodeBox"
    ) {
      openPopup(params);
    }
  };

  const handleKeyboardNavigation = (
    params: SuppressKeyboardEventParams
  ): boolean => {
    const { event, api, column, node } = params;

    if (!event) {
      return false;
    }

    const key = event.key;
    const isEditing = api.getEditingCells().length > 0;
    switch (key) {
      case "Enter":
        api.stopEditing(false);
        moveToNextCell(node.data, column.getColId());
        return true;

      case " ":
      case "Spacebar":
        if (!isEditing && column.getColDef().editable) {
          api.startEditingCell({
            rowIndex: node.rowIndex!,
            colKey: column.getColId(),
            key: event.key,
          });
          event.preventDefault(); // 기본 Space 동작(셀 선택 토글) 차단
          return true; // AG Grid 기본 Space 동작 차단
        }
        break;
      case "ArrowUp":
      case "ArrowDown":
        if (isEditing) {
          params.event.preventDefault();
        }
        return false;
      default:
        return false;
    }

    return false;
  };

  const onCellEditingStopped = (event: any) => {
    if (event.colDef?.field === "fixCheckbox") return;
    const tpColumn = event.colDef?.columnConfig.tp_column;
    if (tpColumn == "SnDateBox" || tpColumn == "SnComboBox") {
      moveToNextCell(event.node.data, event.colDef?.field);
    }
  };

  const handleContextMenu = (params: any) => {
    if (events?.onContextMenu) {
      events?.onContextMenu(params); // 부모 컴포넌트에서 받은 이벤트 실행
    }
  };

  const handleOnRowDoubleClicked = (event: any) => {
    if (events?.onRowDoubleClicked) {
      events?.onRowDoubleClicked(event);
    }
  };
  //#endregion

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter", // 헤더 필터 비활성화 (타입을 명시해야 정상 작동됨.)
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      headerClass: "header-center",
      suppressFlash: true,
      suppressKeyboardEvent: handleKeyboardNavigation,
    }),
    []
  );

  //#region 그리드 렌더링
  const renderGridContent = () => {
    return (
      <div
        className="SnTree"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
        {...find.wrapperProps}
      >
        {/* 찾기 바 (Ctrl+F) */}
        <SnGridFindBar {...find.barProps} />
        {/* 그리드 */}
        <div className="SnTree ag-theme-alpine" style={{ flex: "1 1 auto" }}>
          <AgGridReact
            ref={gridRef}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowData={keyRowData}
            pinnedBottomRowData={initPinnedBottomRowData}
            suppressFocusAfterRefresh={true}
            suppressCellFocus={false}
            rowSelection={rowSelection}
            animateRows={false}
            cellSelection={true}
            suppressNoRowsOverlay={true}
            suppressMenuHide={false}
            localeText={localeText}
            rowGroupPanelShow={
              etcProps?.rowGroupPanelShow || "onlyWhenGrouping"
            }
            domLayout="normal"
            suppressHorizontalScroll={false}
            suppressRowVirtualisation={false}
            popupParent={
              etcProps?.popupParent || document.querySelector("body")
            }
            headerHeight={30}
            rowHeight={30}
            loadingOverlayComponent={"agLoadingOverlay"}
            onGridReady={handleGridReady}
            onCellFocused={handleCellFocused}
            onCellValueChanged={handleInternalCellValueChanged}
            onCellDoubleClicked={handleCellDoubleClicked}
            onCellContextMenu={handleContextMenu}
            onRowDoubleClicked={handleOnRowDoubleClicked}
            onRowDataUpdated={() => {
              if (!isMemoizedRowDataChanged) return;
              if (memoizedRowData && memoizedRowData?.length < 1) return;
              const api = gridApiRef.current;
              if (!api) return;

              requestAnimationFrame(() => {
                gridApiRef.current.clearFocusedCell();
                gridApiRef.current.deselectAll();
                selectAndFocusRow(0);
                if (events?.onRowDataReady) {
                  events.onRowDataReady();
                }
                calculateTotals();
              });

              setIsMemoizedRowDataChanged(false);
            }}
            onModelUpdated={() => {
              if (isDeleteFocusPending.current.pending) {
                const { index: deletedRowIndex, pending } =
                  isDeleteFocusPending.current;
                handleRowFocusAfterDelete(deletedRowIndex);
                isDeleteFocusPending.current = { pending: false, index: 0 };
              }
            }}
            onCellEditingStopped={onCellEditingStopped}
            treeData={true}
            rowDragManaged={true}
            suppressMoveWhenRowDragging={true}
            treeDataParentIdField={treeProps?.treeDataParentIdField}
            groupDefaultExpanded={treeProps?.groupDefaultExpanded}
            autoGroupColumnDef={treeProps?.autoGroupColumnDef}
            getRowId={treeProps?.getRowId}
            getDataPath={treeProps?.getDataPath}
            onRowDragEnd={handleInternalRowDragEnd}
          />
        </div>
      </div>
    );
  };
  //#endregion

  //#region return
  return (
    <>
      {etcProps?.title ? (
        <SnTab
          titles={[etcProps?.title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={events?.onBtnAddClick}
              onBtnDeleteClick={events?.onBtnDeleteClick}
              body={header}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
  //#endregion
});
export default React.memo(SnTree);
